import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, Save, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../ui/alert-dialog';
import { ColorDot, COLOR_LABELS, ACTIONABLE_COLORS } from './colorLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO_BADGE = {
  borrador: 'bg-gray-100 text-gray-700',
  pendiente: 'bg-yellow-100 text-yellow-700',
  aprobado: 'bg-green-100 text-green-700'
};

const AccionRow = ({ accion, onUpdate, onDelete, onChangeEstado }) => {
  const [editing, setEditing] = useState(false);
  const [objetivo, setObjetivo] = useState(accion.objetivo || '');
  const [asunto, setAsunto] = useState(accion.asunto || '');
  const [mensaje, setMensaje] = useState(accion.mensaje || '');

  const save = async () => {
    await onUpdate(accion.id, { objetivo, asunto, mensaje });
    setEditing(false);
  };

  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={`accion-row-${accion.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ColorDot color={accion.color} />
          <span className="font-medium text-sm">{COLOR_LABELS[accion.color] || accion.color}</span>
          <Badge variant="outline" className="text-xs">Semana {accion.semana}</Badge>
          {accion.hito && <Badge variant="outline" className="text-xs">{accion.hito}</Badge>}
          <Badge variant="outline" className="text-xs">{accion.instance}</Badge>
          <Badge className={ESTADO_BADGE[accion.estado]} data-testid={`accion-estado-${accion.id}`}>
            {accion.estado}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`accion-edit-btn-${accion.id}`}>
              Editar
            </Button>
          )}
          {accion.estado !== 'aprobado' ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={!accion.mensaje} data-testid={`accion-approve-btn-${accion.id}`}>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Aprobar (Cande)
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Aprobar esta acción?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Una vez aprobada, esta acción va a poder generar la lista de alumnos en "Listas" (vista previa, no se envía nada todavía).
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onChangeEstado(accion.id, 'aprobado')} data-testid={`accion-approve-confirm-${accion.id}`}>
                    Sí, aprobar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onChangeEstado(accion.id, 'borrador')} data-testid={`accion-unapprove-btn-${accion.id}`}>
              Volver a borrador
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(accion.id)} data-testid={`accion-delete-btn-${accion.id}`}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <Input
            placeholder="Objetivo"
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            data-testid={`accion-objetivo-input-${accion.id}`}
          />
          <Input
            placeholder="Asunto del mail"
            value={asunto}
            onChange={(e) => setAsunto(e.target.value)}
            data-testid={`accion-asunto-input-${accion.id}`}
          />
          <Textarea
            placeholder="Mensaje Mail (podés usar {nombre}, {email}, {portal}, {centro_servicio})"
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            data-testid={`accion-mensaje-input-${accion.id}`}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} data-testid={`accion-save-btn-${accion.id}`}>
              <Save className="h-4 w-4 mr-1" /> Guardar
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-600 space-y-1">
          <p><span className="font-medium">Objetivo:</span> {accion.objetivo || <span className="text-gray-400">(sin definir)</span>}</p>
          <p><span className="font-medium">Canal:</span> Etapa 1: sólo Mail (canal deseado a futuro: {accion.canal_objetivo || 'mail'} - WhatsApp/Discado disponibles en Etapa 2)</p>
          <p><span className="font-medium">Asunto:</span> {accion.asunto || <span className="text-gray-400">(sin definir)</span>}</p>
          <p><span className="font-medium">Mensaje:</span> {accion.mensaje || <span className="text-gray-400">(sin definir)</span>}</p>
          {accion.aprobado_por && (
            <p className="text-xs text-gray-400">Aprobado por {accion.aprobado_por} el {new Date(accion.aprobado_at).toLocaleString('es-AR')}</p>
          )}
        </div>
      )}
    </div>
  );
};

const PersistenciaConfigAcciones = ({ token, instances }) => {
  const [acciones, setAcciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instanceFilter, setInstanceFilter] = useState('');
  const [newRow, setNewRow] = useState({ instance: '', color: 'orange', semana: '1' });

  const loadAcciones = useCallback(async () => {
    setLoading(true);
    try {
      const params = instanceFilter ? `?instance=${instanceFilter}` : '';
      const response = await fetch(`${API_URL}/api/persistencia/config-acciones${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await response.json();
      setAcciones(d.acciones || []);
    } catch (error) {
      console.error('Error cargando config de acciones:', error);
    } finally {
      setLoading(false);
    }
  }, [token, instanceFilter]);

  useEffect(() => { loadAcciones(); }, [loadAcciones]);

  const handleCreate = async () => {
    if (!newRow.instance) {
      toast.error('Elegí una instancia');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/persistencia/config-acciones`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newRow)
      });
      const d = await response.json();
      if (!response.ok) throw new Error(d.detail || 'Error creando la fila');
      toast.success('Fila agregada a la matriz (borrador)');
      loadAcciones();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      const response = await fetch(`${API_URL}/api/persistencia/config-acciones/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Error guardando');
      toast.success('Guardado');
      loadAcciones();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeEstado = async (id, estado) => {
    try {
      const response = await fetch(`${API_URL}/api/persistencia/config-acciones/${id}/estado`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado })
      });
      const d = await response.json();
      if (!response.ok) throw new Error(d.detail || 'Error cambiando el estado');
      toast.success(estado === 'aprobado' ? 'Acción aprobada' : 'Vuelta a borrador');
      loadAcciones();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/persistencia/config-acciones/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error eliminando');
      toast.success('Fila eliminada');
      loadAcciones();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-gray-900">Config de Acciones</h3>
        <p className="text-sm text-gray-500">
          Matriz Instancia × Color × Semana → Objetivo / Canal / Mensaje. Fuente única (no el Google Sheet). Contenido real de CCF cargado (playbook de Cande), en estado "borrador" - revisar y aprobar cada fila antes de que aparezca en "Listas".
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Agregar fila</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={newRow.instance}
            onChange={(e) => setNewRow({ ...newRow, instance: e.target.value })}
            data-testid="new-accion-instance-select"
          >
            <option value="">Instancia...</option>
            {(instances || []).map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
          </select>
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={newRow.color}
            onChange={(e) => setNewRow({ ...newRow, color: e.target.value })}
            data-testid="new-accion-color-select"
          >
            {ACTIONABLE_COLORS.map((c) => <option key={c} value={c}>{COLOR_LABELS[c]}</option>)}
          </select>
          <Input
            placeholder="Semana (1-4, o fuera_de_ciclo)"
            value={newRow.semana}
            onChange={(e) => setNewRow({ ...newRow, semana: e.target.value })}
            data-testid="new-accion-semana-input"
          />
          <Button onClick={handleCreate} data-testid="new-accion-create-btn">
            <Plus className="h-4 w-4 mr-1" /> Agregar
          </Button>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <select
          className="border rounded-md px-3 py-2 text-sm"
          value={instanceFilter}
          onChange={(e) => setInstanceFilter(e.target.value)}
          data-testid="config-acciones-instance-filter"
        >
          <option value="">Todas las instancias</option>
          {(instances || []).map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Cargando matriz...</p>
      ) : acciones.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-400" data-testid="config-acciones-empty">
            No hay filas en la matriz todavía.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="config-acciones-list">
          {acciones.map((a) => (
            <AccionRow
              key={a.id}
              accion={a}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onChangeEstado={handleChangeEstado}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PersistenciaConfigAcciones;
